/**
 * RETOBLOCK REAL-TIME SENSOR DASHBOARD (POPUP MONITOR)
 * Cửa sổ nổi 420px x 480px kéo thả tự do, hiển thị liên tục 10Hz siêu âm, line, màu, quang trở và nút bấm.
 */

import { COLOR_DISPLAY_NAMES } from '../core/ble-protocol.js';

export class MonitorDashboard {
  constructor(options) {
    this.container = options.container;
    this.bleTransport = options.bleTransport;
    this.onOpenOta = options.onOpenOta || null;

    this.isOpen = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.posX = window.innerWidth - 460;
    this.posY = 70;

    this.currentLogs = '';
    this.currentFilter = 'ALL';
    this.isLoadingLogs = false;

    this.init();
  }

  init() {
    this.container.innerHTML = `
      <div class="sensor-dashboard-modal" id="sensor-dashboard-window" style="left: ${this.posX}px; top: ${this.posY}px;">
        <div class="sensor-modal-header" id="sensor-modal-drag-bar">
          <div class="sensor-modal-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/><circle cx="12" cy="12" r="2"/></svg>
            <span>BUỒNG LÁI - THÔNG SỐ CẢM BIẾN ROBOT (10Hz)</span>
          </div>
          <button class="btn-close-modal" id="btn-close-monitor">&times;</button>
        </div>

        <div class="sensor-dashboard-body">
          <!-- 1. Siêu âm HC-SR04 -->
          <div class="sensor-widget">
            <div class="sensor-widget-header">
              <span>CẢM BIẾN SIÊU ÂM (I2C - D18/D5)</span>
              <span id="ultrasonic-status-tag" style="color: var(--color-run-green);">An toàn</span>
            </div>
            <div class="ultrasonic-bar-track">
              <div class="ultrasonic-bar-fill" id="ultrasonic-fill" style="width: 25%;"></div>
            </div>
            <div class="ultrasonic-value-row">
              <span style="font-size: 11px; color: var(--text-secondary);">Phạm vi đo tối ưu: 2 - 50 cm (~28.5Hz)</span>
              <span id="ultrasonic-cm-val" style="font-size: 15px; color: var(--color-primary);">-- cm</span>
            </div>
          </div>

          <!-- 2. Dò Line 3 Mắt -->
          <div class="sensor-widget">
            <div class="sensor-widget-header">
              <span>CẢM BIẾN DÒ ĐƯỜNG (LINE SENSOR 3 MẮT)</span>
              <span style="font-size: 10px; color: var(--text-muted);">GPIO 17, 16, 23</span>
            </div>
            <div class="line-sensors-row">
              <div class="line-sensor-unit">
                <div class="line-sensor-circle" id="line-circle-s1">
                  <div class="line-sensor-led"></div>
                </div>
                <span class="line-sensor-label" id="line-text-s1">S1 (Trái): Trắng</span>
              </div>
              <div class="line-sensor-unit">
                <div class="line-sensor-circle black-detected" id="line-circle-s2">
                  <div class="line-sensor-led"></div>
                </div>
                <span class="line-sensor-label" id="line-text-s2">S2 (Giữa): Đen</span>
              </div>
              <div class="line-sensor-unit">
                <div class="line-sensor-circle" id="line-circle-s3">
                  <div class="line-sensor-led"></div>
                </div>
                <span class="line-sensor-label" id="line-text-s3">S3 (Phải): Trắng</span>
              </div>
            </div>
          </div>

          <!-- 3. Ánh sáng & Cảm biến màu TCS34725 -->
          <div class="sensor-dual-grid">
            <!-- Ánh sáng ADC -->
            <div class="sensor-widget">
              <div class="sensor-widget-header">ÁNH SÁNG (ADC)</div>
              <div style="font-size: 16px; font-weight: 700; color: #FFA000;" id="light-percent-val">68 %</div>
              <div class="light-meter-bar">
                <div class="light-meter-fill" id="light-meter-fill" style="width: 68%;"></div>
              </div>
            </div>

            <!-- Màu sắc TCS34725 -->
            <div class="sensor-widget">
              <div class="sensor-widget-header">MÀU SẮC (I2C)</div>
              <div style="display: flex; gap: 8px; align-items: center;">
                <div class="color-preview-box" id="color-preview-box" style="background-color: #E53935;"></div>
                <div class="color-details-col">
                  <span style="font-weight: 700;" id="color-name-val">RED</span>
                  <span style="color: var(--text-muted);" id="color-rgb-val">R:230 G:30 B:35</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 4. Nút nhấn & Trạng thái Robot -->
          <div class="sensor-widget">
            <div class="sensor-widget-header">NÚT NHẤN TRÊN XE (DIGITAL)</div>
            <div class="buttons-status-row">
              <div class="btn-state-pill" id="btn-state-bt1">
                <span>BT_1 (GPIO 34)</span>
                <span id="bt1-text">NHẢ</span>
              </div>
              <div class="btn-state-pill" id="btn-state-bt2">
                <span>BT_2 (GPIO 35)</span>
                <span id="bt2-text">NHẢ</span>
              </div>
            </div>
          </div>

          <!-- 5. Firmware Robot & Cập nhật OTA -->
          <div class="sensor-widget monitor-ota-widget">
            <div class="sensor-widget-header">
              <span style="display: flex; align-items: center; gap: 6px;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>FIRMWARE ROBOT (OTA)</span>
              </span>
            </div>
            <div class="monitor-ota-row">
              <div class="monitor-ota-info">
                <span class="monitor-ota-title">Cập nhật qua BLE Fast OTA</span>
                <span class="monitor-ota-desc" id="monitor-ota-desc">Nâng cấp không dây trực tiếp lên Robot</span>
              </div>
              <button class="btn-monitor-ota" id="btn-monitor-ota" type="button" title="Mở hộp thoại nâng cấp Firmware cho Robot (BLE Fast OTA)">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <span>Cập nhật OTA</span>
              </button>
            </div>
          </div>

          <!-- 6. Nhật ký hoạt động Robot (RAM 5 phút) -->
          <div class="sensor-widget monitor-log-widget">
            <div class="sensor-widget-header">
              <span style="display: flex; align-items: center; gap: 6px;">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                <span>NHẬT KÝ HOẠT ĐỘNG ROBOT (RAM 5 PHÚT)</span>
              </span>
            </div>
            <div class="monitor-ota-row">
              <div class="monitor-ota-info">
                <span class="monitor-ota-title">Log hoạt động trên RAM ESP32</span>
                <span class="monitor-ota-desc">Chỉ lưu 5 phút gần nhất, tự động xóa log cũ</span>
              </div>
              <button class="btn-monitor-log" id="btn-monitor-log" type="button" title="Mở nhật ký hệ thống 5 phút trên RAM của robot">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                <span>Xem Log</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Popup Modal: Xem Nhật ký RAM 5 Phút -->
      <div class="log-viewer-modal-backdrop" id="log-viewer-backdrop" style="display: none;">
        <div class="log-viewer-dialog">
          <div class="log-viewer-header">
            <div class="log-viewer-title">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M4 17l6-6-6-6"/><path d="M12 19h8"/></svg>
              <span>NHẬT KÝ HOẠT ĐỘNG ROBOT (5 PHÚT GẦN NHẤT TRÊN RAM ESP32)</span>
            </div>
            <button class="log-viewer-close" id="btn-close-log-viewer" title="Đóng">&times;</button>
          </div>
          
          <div class="log-viewer-toolbar">
            <div class="log-filter-group">
              <button class="log-filter-btn active" data-filter="ALL">Tất cả</button>
              <button class="log-filter-btn" data-filter="INFO">INFO</button>
              <button class="log-filter-btn" data-filter="WARN">CẢNH BÁO</button>
              <button class="log-filter-btn" data-filter="ERROR">LỖI</button>
            </div>
            <div class="log-actions-group">
              <button class="log-action-btn" id="btn-refresh-logs" title="Tải lại log mới nhất từ robot">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/></svg>
                <span>Làm mới</span>
              </button>
              <button class="log-action-btn" id="btn-copy-logs" title="Sao chép toàn bộ log vào bộ nhớ tạm">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                <span id="copy-logs-btn-text">Sao chép</span>
              </button>
            </div>
          </div>

          <div class="log-viewer-console-wrapper">
            <pre class="log-viewer-console" id="log-viewer-content">Chưa tải dữ liệu nhật ký.</pre>
          </div>

          <div class="log-viewer-footer">
            <span class="log-viewer-status-text" id="log-viewer-status">Lưu trữ: RAM ESP32 (Không ghi Flash) | Tự động hủy sau 300s</span>
            <button class="btn-log-done" id="btn-done-log-viewer">Đóng</button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  bindEvents() {
    const modal = document.getElementById('sensor-dashboard-window');
    const dragBar = document.getElementById('sensor-modal-drag-bar');
    const closeBtn = document.getElementById('btn-close-monitor');

    // Dragging logic with snap-to-edge (20px)
    dragBar.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.dragStartX = e.clientX - modal.offsetLeft;
      this.dragStartY = e.clientY - modal.offsetTop;
    });

    window.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        let newX = e.clientX - this.dragStartX;
        let newY = e.clientY - this.dragStartY;

        // Snap to right edge
        if (window.innerWidth - (newX + modal.offsetWidth) < 20) {
          newX = window.innerWidth - modal.offsetWidth - 10;
        }
        // Snap to top edge
        if (newY < 70) {
          newY = 60;
        }

        modal.style.left = `${newX}px`;
        modal.style.top = `${newY}px`;
        this.posX = newX;
        this.posY = newY;
      }
    });

    window.addEventListener('mouseup', () => {
      this.isDragging = false;
    });

    closeBtn.addEventListener('click', () => {
      this.hide();
    });

    const otaBtn = document.getElementById('btn-monitor-ota');
    if (otaBtn) {
      otaBtn.addEventListener('click', () => {
        if (this.onOpenOta) {
          this.onOpenOta();
        }
      });
    }

    const logBtn = document.getElementById('btn-monitor-log');
    if (logBtn) {
      logBtn.addEventListener('click', () => {
        this.openLogViewer();
      });
    }

    const closeLogBtn = document.getElementById('btn-close-log-viewer');
    const doneLogBtn = document.getElementById('btn-done-log-viewer');
    const backdrop = document.getElementById('log-viewer-backdrop');
    if (closeLogBtn) closeLogBtn.addEventListener('click', () => this.closeLogViewer());
    if (doneLogBtn) doneLogBtn.addEventListener('click', () => this.closeLogViewer());
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this.closeLogViewer();
      });
    }

    const refreshLogBtn = document.getElementById('btn-refresh-logs');
    if (refreshLogBtn) {
      refreshLogBtn.addEventListener('click', () => this.fetchLogs());
    }

    const copyLogBtn = document.getElementById('btn-copy-logs');
    if (copyLogBtn) {
      copyLogBtn.addEventListener('click', () => this.copyLogs());
    }

    const filterBtns = document.querySelectorAll('.log-filter-btn');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentFilter = btn.dataset.filter || 'ALL';
        this.renderLogs();
      });
    });
  }

  toggle() {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  show() {
    this.isOpen = true;
    const modal = document.getElementById('sensor-dashboard-window');
    if (modal) modal.classList.add('show');
  }

  hide() {
    this.isOpen = false;
    const modal = document.getElementById('sensor-dashboard-window');
    if (modal) modal.classList.remove('show');
  }

  /**
   * Cập nhật dữ liệu từ gói tin Telemetry 0x10 thời gian thực
   */
  updateTelemetry(telemetry) {
    if (!telemetry) return;

    // 1. Cảm biến siêu âm (Giao thức I2C - D18/D5, tối ưu 50cm ~28.5Hz)
    const dist = telemetry.distance;
    const cmValEl = document.getElementById('ultrasonic-cm-val');
    const fillEl = document.getElementById('ultrasonic-fill');
    const statusTag = document.getElementById('ultrasonic-status-tag');

    if (cmValEl && fillEl) {
      // Bắt mã lỗi ngắt kết nối / không tìm thấy cảm biến siêu âm (0xFFFF = 65535 hoặc < 0)
      if (dist === 65535 || dist < 0 || dist === undefined || dist === null) {
        cmValEl.textContent = '-- cm';
        fillEl.style.width = '0%';
        if (statusTag) {
          statusTag.textContent = 'Không tìm thấy cảm biến';
          statusTag.style.color = '#D50000';
        }
      } else {
        cmValEl.textContent = `${dist} cm`;
        // Tỷ lệ đo theo dải chuẩn tối ưu 50cm
        const fillPct = Math.min(100, Math.max(5, (dist / 50) * 100));
        fillEl.style.width = `${fillPct}%`;

        if (statusTag) {
          if (dist < 15) {
            statusTag.textContent = 'Nguy hiểm (< 15cm)';
            statusTag.style.color = '#D50000';
          } else if (dist < 30) {
            statusTag.textContent = 'Cảnh báo (15-30cm)';
            statusTag.style.color = '#FF8F00';
          } else {
            statusTag.textContent = 'An toàn (> 30cm)';
            statusTag.style.color = '#00C853';
          }
        }
      }
    }

    // 2. Cảm biến line 3 mắt
    const s1Circle = document.getElementById('line-circle-s1');
    const s2Circle = document.getElementById('line-circle-s2');
    const s3Circle = document.getElementById('line-circle-s3');
    const s1Text = document.getElementById('line-text-s1');
    const s2Text = document.getElementById('line-text-s2');
    const s3Text = document.getElementById('line-text-s3');

    if (s1Circle && telemetry.line) {
      this.updateLineUnit(s1Circle, s1Text, 'S1 (Trái)', telemetry.line.s1);
      this.updateLineUnit(s2Circle, s2Text, 'S2 (Giữa)', telemetry.line.s2);
      this.updateLineUnit(s3Circle, s3Text, 'S3 (Phải)', telemetry.line.s3);
    }

    // 3. Quang trở
    const lightVal = document.getElementById('light-percent-val');
    const lightFill = document.getElementById('light-meter-fill');
    if (lightVal && lightFill) {
      lightVal.textContent = `${telemetry.lightPercent} %`;
      lightFill.style.width = `${telemetry.lightPercent}%`;
    }

    // 4. Màu sắc (Chuẩn hóa 8 màu STEM theo không gian màu HSV & Trạng thái I2C)
    const colorBox = document.getElementById('color-preview-box');
    const colorNameEl = document.getElementById('color-name-val');
    const colorRgbEl = document.getElementById('color-rgb-val');

    if (colorBox && telemetry.color) {
      const colorId = telemetry.color.id;
      const isDisconnected = (colorId === 255);
      const isBlack = (colorId === 6);

      if (isDisconnected) {
        // Trường hợp 1: Cảm biến màu không phản hồi trên bus I2C lúc boot
        colorBox.classList.add('disconnected');
        colorBox.style.backgroundColor = '';
        if (colorNameEl) {
          colorNameEl.textContent = COLOR_DISPLAY_NAMES[255] || '⚠️ Mất kết nối I2C';
          colorNameEl.style.color = '#FF5722';
        }
        if (colorRgbEl) {
          colorRgbEl.textContent = 'R:0 G:0 B:0 (Không phản hồi I2C)';
        }
      } else if (isBlack) {
        // Trường hợp 2: Môi trường tối / Không có vật cản trước mắt đọc (cảm biến I2C vẫn kết nối tốt)
        colorBox.classList.remove('disconnected');
        colorBox.style.backgroundColor = '#121212';
        if (colorNameEl) {
          colorNameEl.textContent = COLOR_DISPLAY_NAMES[6] || 'ĐEN (BLACK)';
          colorNameEl.style.color = 'var(--text-primary)';
        }
        if (colorRgbEl) {
          colorRgbEl.textContent = `R:${telemetry.color.r} G:${telemetry.color.g} B:${telemetry.color.b} (Tối / Không vật cản)`;
        }
      } else {
        // Trường hợp 3: Nhận diện 8 màu sắc cơ bản (1..8) hoặc 0 (UNKNOWN)
        colorBox.classList.remove('disconnected');
        colorBox.style.backgroundColor = `rgb(${telemetry.color.r}, ${telemetry.color.g}, ${telemetry.color.b})`;
        if (colorNameEl) {
          colorNameEl.textContent = COLOR_DISPLAY_NAMES[colorId] || telemetry.color.name;
          colorNameEl.style.color = '';
        }
        if (colorRgbEl) {
          colorRgbEl.textContent = `R:${telemetry.color.r} G:${telemetry.color.g} B:${telemetry.color.b}`;
        }
      }
    }

    // 5. Nút bấm
    const bt1Pill = document.getElementById('btn-state-bt1');
    const bt2Pill = document.getElementById('btn-state-bt2');
    const bt1Text = document.getElementById('bt1-text');
    const bt2Text = document.getElementById('bt2-text');

    if (bt1Pill && telemetry.buttons) {
      if (telemetry.buttons.bt1) {
        bt1Pill.classList.add('pressed');
        bt1Text.textContent = 'NHẤN';
      } else {
        bt1Pill.classList.remove('pressed');
        bt1Text.textContent = 'NHẢ';
      }

      if (telemetry.buttons.bt2) {
        bt2Pill.classList.add('pressed');
        bt2Text.textContent = 'NHẤN';
      } else {
        bt2Pill.classList.remove('pressed');
        bt2Text.textContent = 'NHẢ';
      }
    }

    // 6. Cập nhật phiên bản Firmware xe nếu nhận được từ telemetry
    if (telemetry.firmwareVersion) {
      const fwBadge = document.getElementById('monitor-fw-badge');
      if (fwBadge) {
        fwBadge.textContent = telemetry.firmwareVersion;
      }
    }
  }

  updateLineUnit(circleEl, textEl, label, isBlack) {
    if (isBlack) {
      circleEl.classList.add('black-detected');
      textEl.textContent = `${label}: Đen`;
      textEl.style.color = '#212121';
    } else {
      circleEl.classList.remove('black-detected');
      textEl.textContent = `${label}: Trắng`;
      textEl.style.color = '#757575';
    }
  }

  openLogViewer() {
    const backdrop = document.getElementById('log-viewer-backdrop');
    if (backdrop) {
      backdrop.style.display = 'flex';
      this.fetchLogs();
    }
  }

  closeLogViewer() {
    const backdrop = document.getElementById('log-viewer-backdrop');
    if (backdrop) {
      backdrop.style.display = 'none';
    }
  }

  async fetchLogs() {
    const contentEl = document.getElementById('log-viewer-content');
    const statusEl = document.getElementById('log-viewer-status');
    if (!contentEl) return;

    if (!this.bleTransport || !this.bleTransport.isConnected) {
      contentEl.innerHTML = `<span class="log-line-warn">[CHÚ Ý] Robot chưa được kết nối Bluetooth BLE.\nVui lòng bấm 'Kết nối Bluetooth' trước khi xem nhật ký.</span>`;
      if (statusEl) statusEl.textContent = 'Trạng thái: Chưa kết nối';
      return;
    }

    contentEl.textContent = 'Đang yêu cầu và tải nhật ký hoạt động từ RAM ESP32 qua BLE...';
    if (statusEl) statusEl.textContent = 'Đang tải stream dữ liệu...';

    try {
      this.isLoadingLogs = true;
      const logs = await this.bleTransport.requestLogs(6000);
      this.currentLogs = logs || '';
      this.renderLogs();
      if (statusEl) {
        const lineCount = (this.currentLogs.trim().match(/\n/g) || []).length + 1;
        statusEl.textContent = `Đã tải ${lineCount} bản ghi | RAM ESP32 (Lưu tối đa 300 giây)`;
      }
    } catch (err) {
      contentEl.innerHTML = `<span class="log-line-error">[LỖI TẢI LOG]: ${err.message || 'Không thể lấy dữ liệu từ Robot.'}</span>`;
      if (statusEl) statusEl.textContent = 'Lỗi kết nối';
    } finally {
      this.isLoadingLogs = false;
    }
  }

  renderLogs() {
    const contentEl = document.getElementById('log-viewer-content');
    if (!contentEl) return;

    if (!this.currentLogs || this.currentLogs.trim().length === 0) {
      contentEl.textContent = 'Không có bản ghi nhật ký nào trong 5 phút qua.';
      return;
    }

    const lines = this.currentLogs.split('\n');
    const filteredLines = lines.filter(line => {
      if (!line.trim()) return false;
      if (this.currentFilter === 'ALL') return true;
      return line.includes(`[${this.currentFilter}]`);
    });

    if (filteredLines.length === 0) {
      contentEl.textContent = `Không có bản ghi nhật ký nào thuộc nhóm [${this.currentFilter}].`;
      return;
    }

    const escapeHtml = (text) => {
      return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    };

    const formattedHtml = filteredLines.map(line => {
      const safe = escapeHtml(line);
      if (safe.includes('[ERROR]')) {
        return `<span class="log-line-error">${safe}</span>`;
      } else if (safe.includes('[WARN]')) {
        return `<span class="log-line-warn">${safe}</span>`;
      } else if (safe.includes('[INFO]')) {
        return `<span class="log-line-info">${safe}</span>`;
      }
      return safe;
    }).join('\n');

    contentEl.innerHTML = formattedHtml;
  }

  async copyLogs() {
    const copyBtnText = document.getElementById('copy-logs-btn-text');
    if (!this.currentLogs) return;

    try {
      await navigator.clipboard.writeText(this.currentLogs);
      if (copyBtnText) {
        const originalText = copyBtnText.textContent;
        copyBtnText.textContent = 'Đã chép!';
        setTimeout(() => {
          copyBtnText.textContent = originalText;
        }, 1500);
      }
    } catch {
      console.warn('Không thể sao chép vào bộ nhớ tạm');
    }
  }
}
